import React, { useContext, useState, useEffect } from "react";
import Logger from '@lmig/dotcom-utilities-helpers/logger';
import Hr from '@lmig/dotcom-aspect-components/Utility/Hr';
import getConfig from 'next/config';
import ComponentMapRender from '@lmig/dotcom-aspect-components/Utility/ComponentMapRender';
import { titleCase } from 'title-case';
import RichText from "@lmig/dotcom-aspect-comparion-components/RichText";
import withPreprocessedContent from "@lmig/dotcom-aspect-comparion-components/Utility/withPreprocessedContent";
import Head from "@lmig/dotcom-aspect-comparion-components/Head";
import Layout from "@lmig/dotcom-aspect-comparion-components/Layout";
import BreadcrumbSticky from "@lmig/dotcom-aspect-comparion-components/BreadcrumbSticky";
import { Button, Heading, Link } from '@lmig/lmds-react';
import OfficesByState from "@lmig/dotcom-aspect-comparion-components/OfficesByState";
import OfficeAgents from "@lmig/dotcom-aspect-comparion-components/Office/OfficeAgents";
import LinkEnvironmentalize from "@lmig/dotcom-aspect-comparion-components/LinkEnvironmentalize";
import { emsGraphQlQuery } from "@lmig/dotcom-aspect-comparion-helpers/emsGraphQlQuery";
import { getServerSideProps as callGetServerSideProps } from "@lmig/dotcom-aspect-comparion-helpers/staticPageController";
import { filterUndefined } from "@lmig/dotcom-aspect-comparion-helpers/filterUndefined";
import { formatOffice, formatAgent } from "@lmig/dotcom-aspect-comparion-helpers/formatEntity";
import { ContextApp } from '@lmig/dotcom-aspect-components/ContextApp';
import stateSpecificContent from "@lmig/dotcom-aspect-comparion-helpers/stateSpecificContent";
import { scrollToElement } from '@lmig/dotcom-aspect-helpers/scroll';
import WrapperSizeable from "@lmig/dotcom-aspect-comparion-components/Utility/WrapperSizeable";
import ArticleWithMap from "@lmig/dotcom-aspect-comparion-components/ArticleWithMap";

const logger = new Logger('client:layout:agents-per-state');
const {
  publicRuntimeConfig: { remoteOffices }
} = getConfig();

const AgentsPerState = withPreprocessedContent((props) => {
  const { location } = useContext(ContextApp);
  let stateName = props.stateName.replaceAll('-', ' ');
  stateName = stateName.replace(/(^\w{1})|(\s+\w{1})/g, letter => letter.toUpperCase());
  const stateContent = stateSpecificContent.find(({ name }) => name === stateName);
  const [stateCities, setStateCities] = useState(stateContent.cities);

  const agents = props.agentInformation;
  const offices = props.officeInformation;
  const officeMap = offices.map(({ alias }, index) => ({ alias, index }));

  const onAnchorClick = (e) => {
    e.preventDefault();
    scrollToElement({
      el: document.querySelector('.headerScrollElement'),
      additionalVerticalOffset: -64,
    });
  };

  useEffect(() => {
    if (location.state && location.city && location.state === stateContent.code && stateCities.every(city => city.toLowerCase() !== location.city.toLowerCase())) {
      setStateCities([location.city.toLowerCase(), ...stateCities]);
    }
  }, [location, stateCities, stateContent.code]);

  const componentMap = {
    wrapperSizeable: WrapperSizeable,
    stateLocationHero: () => <ArticleWithMap
      title={`${stateName} insurance agents`}
      headingTag="h1"
      headingType="h3-bold"
      text={
        <>
          <p>Whether you live in <span className="cities" style={{ "textTransform": "capitalize" }}>{stateCities.join(", ")}</span>, or anywhere else, our local <Link onClick={onAnchorClick}>{stateName} insurance agents</Link> are dedicated to finding the right coverage and will do their best to save you money.</p>
          <p>From {stateContent.concerns.join(", ")}, our agents are familiar with your needs because they live and work here too.</p>
          <p>As one of the largest Personal Lines agencies in the U.S. by premium, Comparion Insurance Agency is proud to serve the diverse communities of {stateName}.</p>
          <p>Contact an insurance agent near you in {stateName} today for multiple quotes for auto, home, life, and more.</p>
          <div data-theme="evolved"><strong><LinkEnvironmentalize><Button size="medium" variant="secondary" href="/quote" rel="nofollow">Start my quote</Button></LinkEnvironmentalize></strong></div>
        </>
      }
      entityData={offices}
      stateName={stateName}
    />,
    richText: RichText,
    hr: ({ ...rest }) => <>
      <Hr {...rest} />
      <WrapperSizeable margin={0}>
        <Heading type="h2" className="headerScrollElement" style={{ maxWidth: '100%' }}>Find an insurance agent in {stateName}</Heading>
      </WrapperSizeable>
    </>,
    breadcrumbSticky: BreadcrumbSticky,
    officesByState: () => offices.length ? <OfficesByState entityData={offices} title titleText={`Our ${stateName} offices`} /> : null,
    officeAgents: () => agents.length ? <OfficeAgents agents={agents} state={stateName} buttonText={{ show: `See more agents in ${stateName}`, hide: "View less" }} buttonMargin officeMap={officeMap} /> : null,
  };

  return (
    <>
      <Head {...props.content.meta} />
      <Layout agentSession={false}>
        <ComponentMapRender content={props.content.mainContent} componentMap={componentMap} />
      </Layout>
    </>
  );
});

const getServerSideProps = async (context) => {
  const resultProps = await callGetServerSideProps(context);
  const stateName = context.query.officeState;
  const cleanStateName = stateName.replaceAll('-', ' ');
  const stateNameWithHyphen = stateName.replace(/%20/g, '-').replace(/ /g, '-');
  const titleCaseStateName = titleCase(cleanStateName);
  let finalAgents = [];

  resultProps.props.stateName = stateName;

  const formattedAgents = [];
  const formattedOffices = [];

  if (remoteOffices.includes(stateNameWithHyphen)) {
    const query = `query {
      remoteAgents: AgentMany(filter:{type:FIELD,active:true,isRemote:true,offices:{alias:"${stateName}"}}){
        alias
        displayName
        firstName
        preferredName
        yextTitle
        title
        email
        phones{type number extension}
        qualifications{title}
        image{src alt}
        repNum
        nNumber
        reviewCount
        rating
        aliasPath
        profile {completeness fields{image}}
      }
    }`;

    const { data: { remoteAgents = [] } } = await emsGraphQlQuery({
      route: 'graphql',
      cacheKeyPrefix: `agentsPerState-remoteAgents-${stateName}`,
      params: { query }
    });

    remoteAgents.map(async agent => {
      formattedAgents.push(filterUndefined(formatAgent(agent)));
    });

    resultProps.props.agentInformation = formattedAgents;
    resultProps.props.officeInformation = [];
  } else {
    const agentFields = "alias displayName firstName preferredName yextTitle title email phones{type number extension} qualifications{title} image{src alt} repNum nNumber reviewCount rating aliasPath profile{completeness fields{image}} isRemote offices {alias}";
    const stateQuery = `query{
      office:AgentMany(limit:500,filter:{active:true,type:FIELD,offices:{address:{state:{name:"${titleCaseStateName}"}}}}){${agentFields}}
      localOffice:AgentMany(limit:500,filter:{active:true,type:FIELD,localOffice:{state:{name:"${titleCaseStateName}"}}}){${agentFields}}
    }`;

    const { data: { office: nonLocal = [], localOffice: local = [] } } = await emsGraphQlQuery({
      cacheKeyPrefix: `agentsPerState-agents-${cleanStateName.replaceAll(" ", "_")}`,
      params: { query: stateQuery }
    });

    const removed = [];

    const officeAgentCounts = {};

    nonLocal.forEach(({ offices, isRemote }) => {
      if (!officeAgentCounts[offices[0].alias]) {
        officeAgentCounts[offices[0].alias] = {
          remote: isRemote ? 1 : 0,
          nonRemote: isRemote ? 0 : 1,
        };
      } else if (isRemote) {
        officeAgentCounts[offices[0].alias].remote += 1;
      } else {
        officeAgentCounts[offices[0].alias].nonRemote += 1;
      }
    });

    local.forEach(({ alias }) => {
      if (nonLocal.find(agent => agent.alias === alias)) {
        const id = nonLocal.map(agent => agent.alias).indexOf(alias);
        nonLocal.splice(id, 1);
        removed.push(alias);
      }
    });

    finalAgents = nonLocal.concat(local);

    finalAgents.map(async agent => {
      formattedAgents.push(filterUndefined(formatAgent(agent)));
    });
    resultProps.props.agentInformation = formattedAgents;

    const officeInfoQuery = `query{
      OfficeMany(filter:{active:true,officeType:FIELD,address:{state:{name:"${titleCaseStateName}"}}}){
      phones{number type callCenter}
      address{ street additionalStreetInfo state{code name} zip city}
      hours
      rating
      reviews{rating}
      alias
      aliasPath
      location{coordinates}
      dailyHours{monday{start,end,isClosed,byAppointmentOnly},tuesday{start,end,isClosed,byAppointmentOnly},wednesday{start,end,isClosed,byAppointmentOnly},thursday{start,end,isClosed,byAppointmentOnly},friday{start,end,isClosed,byAppointmentOnly},saturday{start,end,isClosed,byAppointmentOnly},sunday{start,end,isClosed,byAppointmentOnly}}
      }
    }`;

    const { data: { OfficeMany: officeInfo = [] } } = await emsGraphQlQuery({
      route: 'graphql',
      cacheKeyPrefix: `agentsPerState-officeInformation-${stateName}`,
      params: {
        query: officeInfoQuery
      }
    });

    officeInfo.map(async office => {
      // if the office doesn't only have remote agents
      if (!(officeAgentCounts[office.alias].remote !== 0 && officeAgentCounts[office.alias].nonRemote === 0)) {
        formattedOffices.push(filterUndefined(formatOffice(office)));
      }
    });
    resultProps.props.officeInformation = formattedOffices;
  }

  if (formattedOffices.length === 0 && formattedAgents.length === 0) {
    logger.debug('No offices or agents found, redirecting to insurance agent page');

    context.res.redirect(301, `/insurance-agent`);
    context.res.end();

    return { props: {} };
  }

  resultProps.props.content.meta.description = `Find an insurance agent in ${titleCaseStateName} for expert advice on auto, home, life, and pet insurance. Get free quotes from multiple insurance companies.`;
  resultProps.props.content.meta.title = `${titleCaseStateName} Insurance Agents | Comparion Insurance Agency`;

  return resultProps;
};

export { getServerSideProps };

export default AgentsPerState;